import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, LogOut, User as UserIcon, Globe, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';

export default function Header() {
  const { user, profile, logout, isAdmin, isQuotaExceeded } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { theme } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const checkTouch = () => setIsTouch(mediaQuery.matches || navigator.maxTouchPoints > 0);
    checkTouch();
    mediaQuery.addEventListener('change', checkTouch);
    return () => mediaQuery.removeEventListener('change', checkTouch);
  }, []);

  const isDarkHeaderPage = theme === 'dark';
  const logoSrc = isDarkHeaderPage ? '/logo.png' : '/TREID - Logotipo Negro.svg';

  interface NavChild {
    name: string;
    path: string;
  }

  interface NavItem {
    name: string;
    path: string;
    children?: NavChild[];
  }

  const navItems: NavItem[] = [
    { name: t('nav.intelligence'), path: '/intelligence' },
    { 
      name: t('nav.home'), 
      path: '/',
      children: [
        { name: t('nav.homeSub'), path: '/' },
        { name: t('nav.aboutCompany'), path: '/nuestra-empresa' }
      ]
    },
    { name: t('nav.markets'), path: '/markets' },
    { name: t('nav.compliance'), path: '/compliance' },
    { name: 'Golden Ticket', path: '/golden-ticket' },
    { name: t('nav.events'), path: '/events' },
  ];

  if (isAdmin) {
    navItems.push({ name: 'ADMIN MARKETING', path: '/admin/blog' });
    navItems.push({ name: 'ADMIN DASHBOARD', path: '/admin/dashboard' });
  }

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const headerHeight = 'h-[72px]';

  const FlagSwitcher = ({ className = "" }: { className?: string }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      <button 
        onClick={() => setLanguage('es')}
        aria-label="Cambiar idioma a Español"
        className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all hover:scale-110 ${language === 'es' ? 'border-primary shadow-[0_0_10px_rgba(254,59,31,0.3)]' : 'border-transparent opacity-50'}`}
        title="Español"
      >
        <img src="https://flagcdn.com/w40/mx.png" alt="MX" className="w-full h-full object-cover" loading="lazy" />
      </button>
      <button 
        onClick={() => setLanguage('en')}
        aria-label="Change language to English"
        className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all hover:scale-110 ${language === 'en' ? 'border-primary shadow-[0_0_10px_rgba(254,59,31,0.3)]' : 'border-transparent opacity-50'}`}
        title="English"
      >
        <img src="https://flagcdn.com/w40/us.png" alt="US" className="w-full h-full object-cover" loading="lazy" />
      </button>
    </div>
  );

  return (
    <>
       {isQuotaExceeded && (
        <div id="firestore_quota_banner" className="fixed top-0 left-0 right-0 z-[60] bg-[#FE3B1F] text-white text-[11px] font-medium py-1.5 md:py-2 px-4 shadow-md text-center flex items-center justify-center gap-1.5 flex-wrap">
          <span className="font-extrabold tracking-wider uppercase">✦ {language === 'es' ? 'AVISO DE CAPACIDAD DE BASE DE DATOS' : 'DATABASE QUOTA REMINDER'}:</span>
          <span>
            {language === 'es' 
              ? 'La base de datos gratuita ha superado su límite de lecturas para hoy. Se restablecerá mañana.' 
              : 'The database has reached its daily Spark read limit. It will reset tomorrow.'}
          </span>
          <a 
            href="https://console.firebase.google.com/project/gen-lang-client-0560969930/firestore/databases/ai-studio-dd28ca17-25eb-4994-8368-9aafe8620ec7/data?openUpgradeDialog=true" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="underline font-black hover:text-white/80 select-all ml-1"
          >
            {language === 'es' ? 'Ver Base de Datos / Actualizar' : 'View Database / Upgrade'} &rarr;
          </a>
        </div>
      )}
      <nav className={`fixed top-0 w-full z-50 flex justify-between items-center px-4 md:px-6 lg:px-8 xl:px-12 ${headerHeight} ${isDarkHeaderPage ? 'bg-zinc-950/95 border-b border-zinc-900' : 'bg-white/85 backdrop-blur-md border-b border-border-glass'} shadow-sm transition-all duration-300 ${isQuotaExceeded ? 'mt-[38px] md:mt-[44px]' : ''}`}>
      <div className="flex items-center gap-3 md:gap-4 lg:gap-6 xl:gap-10">
        <Link to="/" className="flex items-center flex-shrink-0">
          {!logoError ? (
            <img 
              src={logoSrc} 
              alt="TREID" 
              width="160"
              height="40"
              className={`w-auto object-contain transition-all -translate-x-1 ${isDarkHeaderPage ? 'h-[28px] md:h-[32px] lg:h-[40px]' : 'h-[40px] md:h-[48px] lg:h-[54px]'}`} 
              key={theme + (isDarkHeaderPage ? '-dark-hdr' : '')}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="text-lg md:text-xl font-bold tracking-tighter flex items-center">
              <span className="text-primary mr-1">●</span> 
              <span className="font-hanken text-on-surface uppercase italic">TREID</span>
            </div>
          )}
        </Link>
        <div className={`${isTouch ? 'hidden' : 'hidden min-[1200px]:flex'} ${isAdmin ? 'flex-col items-center my-2' : 'items-center'} gap-x-6 gap-y-1`}>
          <div className="flex items-center gap-2 min-[1200px]:gap-6">
              {navItems.filter(item => !item.name.includes('ADMIN')).map((item) => {
              const hasSub = item.children && item.children.length > 0;
              const isItemActive = hasSub 
                ? item.children?.some(c => location.pathname === c.path)
                : location.pathname === item.path;

              if (hasSub) {
                return (
                  <div 
                    key={item.path}
                    onMouseEnter={() => setActiveDropdown(item.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                    className="relative py-2 px-1 cursor-pointer"
                  >
                    <Link
                      to={item.path}
                      className={`label-caps text-[9px] min-[1200px]:text-[11px] transition-colors flex items-center gap-1 whitespace-nowrap font-semibold ${
                        isItemActive ? 'text-primary font-black' : (isDarkHeaderPage ? 'text-white' : 'text-zinc-950')
                      }`}
                    >
                      {item.name}
                      <ChevronDown size={10} className={`transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180 text-primary' : (isDarkHeaderPage ? 'text-white' : 'text-zinc-950')}`} />
                    </Link>
                    {isItemActive && (
                      <motion.div
                        layoutId={`nav-underline-${item.name}`}
                        className="absolute bottom-0 left-1 right-1 h-0.5 bg-primary"
                      />
                    )}

                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                          className={`absolute left-1/2 -translate-x-1/2 mt-2 w-48 border rounded-xl p-2 shadow-2xl z-50 overflow-hidden ${
                            isDarkHeaderPage 
                              ? 'bg-zinc-950 border-zinc-800' 
                              : 'bg-white border-zinc-100'
                          }`}
                        >
                          {item.children?.map((child) => {
                            const isChildActive = location.pathname === child.path;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`flex items-center px-4 py-2.5 rounded-lg transition-colors text-[10px] min-[1200px]:text-[11px] font-bold uppercase tracking-wider text-left ${
                                  isChildActive 
                                    ? 'bg-primary text-white hover:text-white' 
                                    : isDarkHeaderPage
                                      ? 'text-white/80 hover:text-white hover:bg-white/5'
                                      : 'text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100'
                                }`}
                              >
                                <span>{child.name}</span>
                              </Link>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`label-caps text-[9px] min-[1200px]:text-[11px] transition-colors relative py-2 whitespace-nowrap ${
                    location.pathname === item.path ? 'text-primary' : (isDarkHeaderPage ? 'text-white' : 'text-zinc-950')
                  }`}
                >
                  {item.name}
                  {location.pathname === item.path && (
                    <motion.div
                      layoutId={`nav-underline-${item.name}`}
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {isAdmin && (
            <div className="flex items-center gap-2 min-[1200px]:gap-6">
              {navItems.filter(item => item.name.includes('ADMIN')).map((item) => {
                const isItemActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`label-caps text-[9px] min-[1200px]:text-[11px] transition-colors relative py-2 whitespace-nowrap ${
                      isItemActive ? 'text-primary' : (isDarkHeaderPage ? 'text-white' : 'text-zinc-950')
                    }`}
                  >
                    {item.name}
                    {isItemActive && (
                      <motion.div
                        layoutId={`nav-underline-${item.name}`}
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-3 min-[1200px]:gap-6">
        <ThemeToggle />
        <FlagSwitcher className={`${isTouch ? 'hidden' : 'hidden min-[1200px]:flex'}`} />
        
        <div className={`${isTouch ? 'hidden' : 'hidden min-[1200px]:flex'} items-center gap-2 min-[1200px]:gap-6`}>
          {user ? (
            <div className="relative">
              <button 
                onClick={() => setProfileOpen(!profileOpen)}
                aria-label="Abrir menú de perfil"
                className="flex items-center gap-3 bg-surface-glass hover:bg-surface-bright px-4 py-2 rounded-full transition-all border border-border-glass"
              >
                <img src={user.photoURL || `https://ui-avatars.com/api/?name=${user.displayName}`} className="w-6 h-6 rounded-full" alt="profile" loading="lazy" />
                <span className="label-caps text-[10px] text-zinc-700 dark:text-white font-bold">{user.displayName?.split(' ')[0]}</span>
              </button>

              <AnimatePresence>
                {profileOpen && (
                  <>
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-40"
                      onClick={() => setProfileOpen(false)}
                    />
                    <motion.div 
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-4 w-64 bg-surface-container border border-border-glass rounded-2xl p-4 shadow-2xl z-50 overflow-hidden"
                    >
                      <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border-glass">
                        <img src={user.photoURL || `https://ui-avatars.com/api/?name=${user.displayName}`} className="w-10 h-10 rounded-full" alt="" loading="lazy" />
                        <div>
                          <div className="text-sm font-bold truncate">{user.displayName}</div>
                          <div className="text-[10px] text-on-surface-variant uppercase font-bold tracking-widest">
                            {profile?.role === 'admin' 
                              ? 'Administrador' 
                              : profile?.role === 'top' 
                              ? 'Nivel Top' 
                              : profile?.role === 'nivel_2' || profile?.role === 'nivel2' 
                              ? 'Nivel 2' 
                              : profile?.role === 'nivel_1' || profile?.role === 'nivel1' 
                              ? 'Nivel 1'
                              : (profile?.role || 'Miembro')}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Link 
                          to="/profile" 
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 p-3 hover:bg-surface-glass rounded-xl transition-colors text-xs font-bold uppercase tracking-wider text-on-surface-variant hover:text-on-surface"
                        >
                          <UserIcon size={14} className="text-primary" />
                          {t('nav.profile')}
                        </Link>
                        <button 
                          onClick={() => { logout(); setProfileOpen(false); }}
                          className="w-full flex items-center gap-3 p-3 hover:bg-primary/10 rounded-xl transition-colors text-xs font-bold uppercase tracking-wider text-primary"
                        >
                          <LogOut size={14} />
                          {t('nav.logout')}
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link 
              to="/auth"
              className="label-caps text-[10px] min-[1200px]:text-[12px] text-[#e1321b] dark:text-[#e7bdb5] font-bold hover:opacity-85 hover:scale-105 transition-all px-2 no-underline"
            >
              {t('nav.login')}
            </Link>
          )}
          <Link to="/consultation-booking" className="primary-button text-[10px] min-[1200px]:text-sm !py-2 !px-3 min-[1200px]:!px-6 whitespace-nowrap outline-none no-underline">{t('nav.consultancy')}</Link>
        </div>

        <div className={`${isTouch ? 'flex' : 'flex min-[1200px]:hidden'} items-center`}>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
            className="text-on-surface p-2 hover:bg-surface-glass rounded-full transition-colors"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`fixed ${
              isQuotaExceeded 
                ? 'top-[112px] md:top-[104px] h-[calc(100dvh-112px)] md:h-[calc(100dvh-104px)]' 
                : 'top-[72px] h-[calc(100dvh-72px)]'
            } left-0 w-full ${isDarkHeaderPage ? 'bg-zinc-950 text-white' : 'bg-white text-zinc-950'} border-b border-border-glass shadow-2xl flex flex-col p-8 gap-8 ${isTouch ? '' : 'min-[1200px]:hidden'} z-50 overflow-y-auto`}
          >
            <div className="flex justify-end items-center min-[1200px]:hidden">
              <div className="flex items-center gap-4">
                <FlagSwitcher />
              </div>
            </div>
            
            {navItems.map((item) => {
              const hasSub = item.children && item.children.length > 0;
              const isItemActive = hasSub 
                ? item.children?.some(c => location.pathname === c.path)
                : location.pathname === item.path;

              if (hasSub) {
                return (
                  <div key={item.path} className="space-y-2">
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-xl font-hanken font-bold uppercase tracking-widest text-zinc-700 dark:text-white/80 flex items-center gap-1.5 hover:text-primary transition-colors cursor-pointer"
                    >
                      {item.name}
                      <ChevronDown size={14} className="text-primary/75" />
                    </Link>
                    <div className="pl-4 flex flex-col gap-2 border-l border-primary/20">
                      {item.children?.map((child) => {
                        const isChildActive = location.pathname === child.path;
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`text-lg font-hanken font-extrabold uppercase tracking-widest py-1 ${
                              isChildActive ? 'text-primary' : 'text-on-surface hover:text-primary transition-colors'
                            }`}
                          >
                            {child.name}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-2xl font-hanken font-extrabold uppercase tracking-widest ${
                    isItemActive ? 'text-primary' : 'text-on-surface'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
            <div className="h-px w-full bg-border-glass" />
            <div className="flex flex-col gap-6">
              {user ? (
                <>
                  <div className="flex items-center gap-4 p-4 bg-surface-glass rounded-2xl">
                    <img src={user.photoURL || `https://ui-avatars.com/api/?name=${user.displayName}`} className="w-12 h-12 rounded-full" alt="" loading="lazy" />
                    <div>
                      <div className="text-lg font-bold">{user.displayName}</div>
                      <div className="text-xs text-on-surface-variant font-bold uppercase tracking-widest">
                        {profile?.role === 'admin' 
                          ? 'Administrador' 
                          : profile?.role === 'top' 
                          ? 'Nivel Top' 
                          : profile?.role === 'nivel_2' || profile?.role === 'nivel2' 
                          ? 'Nivel 2' 
                          : profile?.role === 'nivel_1' || profile?.role === 'nivel1' 
                          ? 'Nivel 1'
                          : (profile?.role || 'Miembro')}
                      </div>
                    </div>
                  </div>
                  <Link 
                    to="/profile" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-left label-caps text-xl text-on-surface flex items-center gap-3"
                  >
                    <UserIcon size={20} /> {t('nav.profile')}
                  </Link>
                  <button 
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="text-left label-caps text-xl text-primary flex items-center gap-3"
                  >
                    <LogOut size={20} /> {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link 
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-left label-caps text-xl text-on-surface-variant no-underline"
                >
                  {t('nav.login')}
                </Link>
              )}
              <Link to="/consultation-booking" onClick={() => setMobileMenuOpen(false)} className="primary-button text-center text-lg py-4 no-underline">{t('nav.consultancy')}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    </>
  );
}

